import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API } from '../context/AuthContext';
import { DealCard } from '../components/DealCard';
import { AdBannerSidebar } from '../components/AdBanner';
import { RandomProducts } from '../components/RandomProducts';
import { ArticleCards } from '../components/ArticleCards';
import { SEO } from '../components/SEO';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  ArrowLeft, Clock, Eye, User, Tag, ExternalLink, Flame, Loader2, MessageSquare, Send, Trash2, Lightbulb
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { pl } from 'date-fns/locale';
import { toast } from 'sonner';

export default function BlogArticlePage() {
  const { slug } = useParams();
  const { user, token, authHeaders } = useAuth();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [sending, setSending] = useState(false);
  const [commentsTotal, setCommentsTotal] = useState(0);

  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const res = await axios.get(`${API}/blog/articles/${slug}`);
        setArticle(res.data);
        setCommentsTotal(res.data.comments_count || 0);
      } catch {}
      setLoading(false);
    };
    fetchAll();
  }, [slug]);

  useEffect(() => {
    if (article) fetchComments();
  }, [article]);

  const fetchComments = async () => {
    try {
      const res = await axios.get(`${API}/blog/${article.id}/comments`);
      setComments(res.data.comments);
      setCommentsTotal(res.data.total);
    } catch {}
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    if (!token) { toast.error('Zaloguj się aby komentować'); return; }
    setSending(true);
    try {
      await axios.post(`${API}/blog/${article.id}/comments`, { text: newComment }, { headers: authHeaders() });
      setNewComment('');
      fetchComments();
      toast.success('+2 Zorki za komentarz!');
    } catch { toast.error('Błąd dodawania komentarza'); }
    setSending(false);
  };

  const deleteComment = async (id) => {
    try {
      await axios.delete(`${API}/blog/comments/${id}`, { headers: authHeaders() });
      fetchComments();
      toast.success('Komentarz usunięty');
    } catch {}
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div>;
  if (!article) return <div className="text-center py-20 text-muted-foreground">Artykuł nie znaleziony</div>;

  const coverImg = article.cover_image_local ? `${BACKEND}${article.cover_image_local}` : article.cover_image_url;

  const renderInline = (str) => {
    const parts = str.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, j) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={j} className="text-white font-bold">{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  const renderContent = (text) => {
    if (!text) return null;
    return text.split('\n').map((line, i) => {
      // Image embed: ![alt](url)
      const imgMatch = line.match(/^!\[([^\]]*)\]\(([^)]+)\)/);
      if (imgMatch) {
        const imgSrc = imgMatch[2].startsWith('/api') ? `${BACKEND}${imgMatch[2]}` : imgMatch[2];
        return (
          <figure key={i} className="my-4">
            <img src={imgSrc} alt={imgMatch[1]} className="w-full max-h-96 object-cover bg-secondary" loading="lazy" />
            {imgMatch[1] && <figcaption className="text-xs text-muted-foreground mt-1 text-center">{imgMatch[1]}</figcaption>}
          </figure>
        );
      }
      if (line.startsWith('## ')) return <h2 key={i} className="font-heading text-xl font-bold text-white mt-6 mb-3">{renderInline(line.slice(3))}</h2>;
      if (line.startsWith('### ')) return <h3 key={i} className="font-heading text-lg font-bold text-white mt-4 mb-2">{renderInline(line.slice(4))}</h3>;
      if (line.startsWith('- ')) return <li key={i} className="text-foreground/80 ml-4 list-disc">{renderInline(line.slice(2))}</li>;
      if (line.trim() === '') return <br key={i} />;
      return <p key={i} className="text-foreground/80 leading-relaxed my-2">{renderInline(line)}</p>;
    });
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 pb-20 md:pb-6" data-testid="blog-article-page">
      <SEO
        title={article.title}
        description={article.excerpt?.slice(0, 160)}
        image={coverImg}
        type="article"
      />
      <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-white mb-4 transition-colors" data-testid="back-to-blog">
        <ArrowLeft className="w-4 h-4" /> Wróć do bloga
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-8">
          <article data-testid="article-content">
            {coverImg && (
              <div className="w-full h-56 sm:h-72 bg-secondary overflow-hidden mb-6">
                <img src={coverImg} alt={article.title} className="w-full h-full object-cover" />
              </div>
            )}

            <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-white mb-3">
              {article.title}
            </h1>

            <div className="flex items-center flex-wrap gap-3 text-sm text-muted-foreground mb-6 pb-4 border-b border-border/30">
              <span className="flex items-center gap-1"><User className="w-3.5 h-3.5" /> {article.author_nick}</span>
              <span className="flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" />
                {formatDistanceToNow(new Date(article.created_at), { addSuffix: true, locale: pl })}
              </span>
              <span className="flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> {article.views}</span>
              <span className="flex items-center gap-1"><MessageSquare className="w-3.5 h-3.5" /> {commentsTotal}</span>
            </div>

            {article.tags?.length > 0 && (
              <div className="flex gap-2 flex-wrap mb-6">
                {article.tags.map(tag => (
                  <Link key={tag} to={`/blog?tag=${tag}`} className="flex items-center gap-1 text-xs px-2.5 py-1 bg-primary/10 text-primary/80 border border-primary/20 hover:bg-primary/20 transition-colors">
                    <Tag className="w-3 h-3" /> {tag}
                  </Link>
                ))}
              </div>
            )}

            <div data-testid="article-body" className="article-content">
              {article.is_html ? (
                <div dangerouslySetInnerHTML={{ __html: article.content }} />
              ) : (
                renderContent(article.content)
              )}
            </div>

            {/* Featured Deals */}
            {article.featured_deals?.length > 0 && (
              <div className="mt-8 pt-6 border-t border-border/30" data-testid="featured-deals">
                <h2 className="font-heading text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <Flame className="w-5 h-5 text-primary" /> Polecane produkty z artykułu
                </h2>
                <div className="space-y-3">
                  {article.featured_deals.map(deal => <DealCard key={deal.id} deal={deal} />)}
                </div>
              </div>
            )}
          </article>

          {/* Comments Section */}
          <div className="mt-8 pt-6 border-t border-border/30" data-testid="blog-comments-section">
            <h2 className="font-heading text-xl font-bold text-white mb-4 flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-primary" /> Komentarze ({commentsTotal})
            </h2>

            {token ? (
              <form onSubmit={handleComment} className="flex gap-2 mb-6">
                <Input
                  data-testid="blog-comment-input"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Napisz komentarz..."
                  className="flex-1 bg-input border-transparent focus:border-primary rounded-none"
                />
                <Button data-testid="blog-comment-submit" type="submit" disabled={sending} className="bg-primary text-primary-foreground rounded-none px-4">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </form>
            ) : (
              <p className="text-sm text-muted-foreground mb-6">
                <Link to="/auth" className="text-primary hover:underline">Zaloguj się</Link> aby komentować (+2 Zorki)
              </p>
            )}

            <div className="space-y-3">
              {comments.map(c => (
                <div key={c.id} className="bg-card border border-border/30 p-4" data-testid={`blog-comment-${c.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-secondary flex items-center justify-center">
                        <User className="w-3.5 h-3.5 text-muted-foreground" />
                      </div>
                      <span className="text-sm font-medium text-white">{c.user_nick}</span>
                      <span className="text-xs text-muted-foreground">
                        {c.created_at ? formatDistanceToNow(new Date(c.created_at), { addSuffix: true, locale: pl }) : ''}
                      </span>
                    </div>
                    {(user?.id === c.user_id || user?.role === 'admin') && (
                      <button onClick={() => deleteComment(c.id)} className="text-muted-foreground hover:text-destructive transition-colors" data-testid={`delete-blog-comment-${c.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                  <p className="text-sm text-foreground/80">{c.text}</p>
                </div>
              ))}
              {comments.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-6">Brak komentarzy. Bądź pierwszy!</p>
              )}
            </div>
          </div>

          {/* Related articles */}
          {article && (
            <ArticleCards
              title="Powiązane artykuły"
              context={`${article.title} ${(article.tags || []).join(' ')}`}
              excludeId={article.id}
              limit={4}
            />
          )}
        </div>

        {/* Sidebar */}
        <aside className="lg:col-span-4" data-testid="article-sidebar">
          <div className="lg:sticky lg:top-20 space-y-4">
            {/* Random products */}
            <RandomProducts count={3} vertical />

            {/* Referral promo */}
            <div className="bg-card border border-primary/30 p-4 text-center" data-testid="sidebar-ad">
              <div className="bg-primary/10 border border-primary/20 p-5 flex flex-col items-center">
                <Flame className="w-7 h-7 text-primary mb-2" />
                <p className="font-heading text-sm font-bold text-white mb-1">Polecaj Znajdownik!</p>
                <p className="text-xs text-muted-foreground mb-3">Zarabiaj Zorki za polecenia</p>
                <Link to="/referral" className="bg-primary text-primary-foreground px-4 py-1.5 text-xs font-heading font-bold uppercase tracking-wider hover:bg-primary/90 transition-colors" data-testid="sidebar-referral-btn">
                  Polecaj i zarabiaj
                </Link>
              </div>
            </div>

            {/* Latest Deals */}
            <div className="bg-card border border-border/40 p-4" data-testid="sidebar-deals">
              <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-white mb-3 flex items-center gap-2">
                <Flame className="w-4 h-4 text-primary" /> Najnowsze okazje
              </h3>
              <div className="space-y-3">
                {(article.sidebar_deals || []).map(deal => {
                  const img = deal.image_local_path ? `${BACKEND}${deal.image_local_path}` : deal.image_url;
                  return (
                    <Link key={deal.id} to={`/okazja/${deal.slug || deal.id}`} className="flex gap-3 group" data-testid={`sidebar-deal-${deal.id}`}>
                      {img && (
                        <div className="w-16 h-16 bg-secondary flex-shrink-0 overflow-hidden">
                          <img src={img} alt="" className="w-full h-full object-cover" loading="lazy" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-white group-hover:text-primary transition-colors line-clamp-2 font-medium">{deal.title}</p>
                        {deal.deal_price && <p className="price-tag text-sm text-primary mt-0.5">{deal.deal_price} zł</p>}
                        {(deal.affiliate_url || deal.url) && (
                          <span className="text-[10px] text-muted-foreground flex items-center gap-0.5 mt-0.5">
                            <ExternalLink className="w-2.5 h-2.5" /> Sprawdź ofertę
                          </span>
                        )}
                      </div>
                    </Link>
                  );
                })}
              </div>
              <Link to="/" className="block text-center text-xs text-primary hover:text-primary/80 mt-3 pt-3 border-t border-border/20 font-heading uppercase tracking-wider transition-colors">
                Zobacz wszystkie okazje
              </Link>
            </div>

            {/* Wachalski promo */}
            <div className="bg-card border border-border/40 p-4 text-center" data-testid="sidebar-wachalski">
              <Lightbulb className="w-6 h-6 text-zorki mx-auto mb-1" />
              <p className="font-heading text-sm font-bold text-white mb-1">Szukasz czegoś?</p>
              <p className="text-xs text-muted-foreground mb-3">Wąchalski AI znajdzie to za Ciebie!</p>
              <Link to="/wachalski" className="inline-block bg-secondary text-secondary-foreground px-4 py-1.5 text-xs font-heading font-bold uppercase tracking-wider hover:bg-secondary/80 transition-colors">
                Szukaj z AI
              </Link>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
